import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { apiFetch } from "@/lib/api-client";
import { ChatThread, ChatThreadsResponse } from "@/types/chat";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

const CHAT_THREADS_BASE = `${config.isometricApiUrl}/chat/threads`;
const CHAT_THREADS_PAGE_LIMIT = 50;

/**
 * The list of threads is keyed by (uuid, agent, chatMode). Rename / delete /
 * send-first-message invalidate the (uuid, agent) prefix so ALL mode buckets
 * refresh together — cheap, and avoids leaking a fresh thread into the wrong
 * mode bucket if the caller forgets to pass chatMode on invalidate.
 *
 * Called with `chatMode` → full 4-element cache key.
 * Called without   → 3-element prefix used for invalidation. Returning a
 * shorter array (rather than padding with `null`) is what makes TanStack
 * Query's partial-match invalidation actually hit every mode bucket. A
 * `[..., null]` key would only invalidate a key that literally ends in
 * `null`, silently missing the real cached `[..., "general_v3"]` entries
 * and leaving the sidebar stale after a send / rename / delete.
 */
export function chatThreadsQueryKey(uuid: string, agent: string): readonly ["chat-threads", string, string];
export function chatThreadsQueryKey(
  uuid: string,
  agent: string,
  chatMode: string,
  isDeepAnalysis?: boolean,
): readonly ["chat-threads", string, string, string, boolean];
export function chatThreadsQueryKey(
  uuid: string,
  agent: string,
  chatMode?: string,
  isDeepAnalysis: boolean = false,
) {
  // isDeepAnalysis is part of the full key so the deep-analysis thread list is
  // cached separately from the regular one under the same mode. The 2-element
  // (uuid, agent) prefix used for invalidation still partial-matches both.
  return chatMode === undefined
    ? (["chat-threads", uuid, agent] as const)
    : (["chat-threads", uuid, agent, chatMode, isDeepAnalysis] as const);
}

async function throwOnBadResponse(response: Response): Promise<Response> {
  if (!response.ok) {
    const error: any = new Error(`HTTP error! status: ${response.status}`);
    error.status = response.status;
    try {
      const body = await response.json();
      if (body?.message) error.message = body.message;
    } catch {
      // response body wasn't JSON — keep the generic message
    }
    throw error;
  }
  return response;
}

async function fetchThreads(
  uuid: string,
  agent: string,
  token: string,
  chatMode?: string,
  isDeepAnalysis?: boolean,
  page: number = 1,
): Promise<ChatThreadsResponse> {
  const params = new URLSearchParams({
    uuid,
    agent,
    page: String(page),
    limit: String(CHAT_THREADS_PAGE_LIMIT),
  });
  if (chatMode) params.set("chatMode", chatMode);
  // Separate deep-analysis threads from regular ones. Only sent when a value is
  // provided so callers that don't care keep the unfiltered behaviour.
  if (typeof isDeepAnalysis === "boolean") {
    params.set("isDeepAnalysis", String(isDeepAnalysis));
  }
  const response = await apiFetch(`${CHAT_THREADS_BASE}?${params.toString()}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  await throwOnBadResponse(response);
  return response.json();
}

async function createThread(
  body: { uuid: string; agent: string; title?: string },
  token: string
): Promise<ChatThread> {
  const response = await apiFetch(CHAT_THREADS_BASE, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  await throwOnBadResponse(response);
  return response.json();
}

async function renameThread(
  threadId: string,
  title: string,
  token: string
): Promise<ChatThread> {
  const response = await apiFetch(`${CHAT_THREADS_BASE}/${threadId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ title }),
  });
  await throwOnBadResponse(response);
  return response.json();
}

async function deleteThread(threadId: string, token: string): Promise<void> {
  const response = await apiFetch(`${CHAT_THREADS_BASE}/${threadId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  await throwOnBadResponse(response);
}

async function cancelThreadRun(
  threadId: string,
  token: string
): Promise<{ cancelled: number }> {
  const response = await apiFetch(`${CHAT_THREADS_BASE}/${threadId}/cancel`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  await throwOnBadResponse(response);
  return response.json();
}

export function useChatThreadsQuery(
  uuid: string,
  agent: string,
  chatMode?: string,
  enabled: boolean = true,
  isDeepAnalysis?: boolean,
) {
  const { getAuthHeaders } = useAuthHeaders();
  // Paged, newest activity first: the sidebar asks for the next page as the user scrolls down, so a user with
  // thousands of threads can still reach the old ones. A thread whose activity moved it to an earlier page while
  // paging would come back twice, so the pages are merged by threadId.
  return useInfiniteQuery({
    queryKey:
      chatMode === undefined
        ? chatThreadsQueryKey(uuid, agent)
        : chatThreadsQueryKey(uuid, agent, chatMode, isDeepAnalysis),
    queryFn: ({ pageParam }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return fetchThreads(uuid, agent, token, chatMode, isDeepAnalysis, pageParam);
    },
    initialPageParam: 1,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + (p.data?.length ?? 0), 0);
      return (last.data?.length ?? 0) > 0 && loaded < (last.total ?? 0) ? pages.length + 1 : undefined;
    },
    select: (pages): ChatThreadsResponse => {
      const seen = new Set<string>();
      const data = pages.pages
        .flatMap((p) => p.data ?? [])
        .filter((t) => !seen.has(t.threadId) && !!seen.add(t.threadId));
      return { data, total: pages.pages[pages.pages.length - 1]?.total ?? data.length };
    },
    enabled: enabled && !!uuid,
  });
}

export function useCreateChatThread(uuid: string, agent: string) {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title?: string) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return createThread({ uuid, agent, title }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatThreadsQueryKey(uuid, agent) });
    },
  });
}

export function useRenameChatThread(uuid: string, agent: string) {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return renameThread(threadId, title, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatThreadsQueryKey(uuid, agent) });
    },
  });
}

/** Stop: ends the thread's in-flight answer on the server. */
export function useCancelChatRun() {
  const { getAuthHeaders } = useAuthHeaders();
  return useMutation({
    mutationFn: (threadId: string) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return cancelThreadRun(threadId, token);
    },
  });
}

export function useDeleteChatThread(uuid: string, agent: string) {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return deleteThread(threadId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatThreadsQueryKey(uuid, agent) });
    },
  });
}
